import React, { useEffect, useState } from "react";
import DataGrid from './ui/DataTable';
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Tooltip,
  Fab,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip
} from "@mui/material";
import Grid from '@mui/material/Grid2';

// datepicker
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/de';

import Avatar from '@mui/material/Avatar';
import AvatarGroup from '@mui/material/AvatarGroup';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AddIcon from '@mui/icons-material/Add';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import TaskIcon from '@mui/icons-material/Task';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import NoteIcon from '@mui/icons-material/Note';
import EditIcon from '@mui/icons-material/Edit';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import ContactsOutlinedIcon from '@mui/icons-material/ContactsOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import axiosInstance from './axiosInstance';
import { alpha } from '@mui/material/styles';
import { SnackbarProvider, useSnackbar } from "../providers/SnackbarProvider";
import { useTheme } from '@mui/material/styles';
import { useBreadcrumbs } from './BreadcrumbBar';
import EmptyState from './ui/EmptyState';

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Objekttypen-Konfiguration
const OBJECT_TYPES = [
  { value: 'lead', label: 'Lead', icon: <PersonSearchOutlinedIcon />, color: '#7C3AED', endpoint: '/leads/' },
  { value: 'contact', label: 'Kontakt', icon: <ContactsOutlinedIcon />, color: '#0891B2', endpoint: '/contacts/' },
  { value: 'account', label: 'Unternehmen', icon: <BusinessIcon />, color: '#059669', endpoint: '/accounts/' },
  { value: 'project', label: 'Projekt', icon: <ExtensionOutlinedIcon />, color: '#d9a700ff', endpoint: '/projects/'},
];

function getObjectLabel(obj, type) {
  if (!obj) return '-';
  if (type === 'lead') {
    return `${obj.firstName || obj.first_name || ''} ${obj.lastName || obj.last_name || ''}`.trim() || obj.email || `Lead #${obj.id}`;
  }
  if (type === 'contact') {
    return `${obj.first_name || ''} ${obj.last_name || ''}`.trim() || obj.email || `Kontakt #${obj.id}`;
  }
  if (type === 'account') {
    return obj.name || obj.company_name || `Account #${obj.id}`;
  }
  return obj.name || obj.email || `#${obj.id}`;
}

function InteractionList() {
  const { showSnackbar } = useSnackbar();
  const [dateError, setDateError] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState({});
  const [contacts, setContacts] = useState({});
  const [selectedInteraction, setSelectedInteraction] = useState(null);
  const [originalInteraction, setOriginalInteraction] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [interactionToDelete, setInteractionToDelete] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [users, setUsers] = useState([]);

  // Objektzuweisung States
  const [selectedObjectType, setSelectedObjectType] = useState('');
  const [selectedObjectId, setSelectedObjectId] = useState('');
  const [objectList, setObjectList] = useState([]);
  const [objectListLoading, setObjectListLoading] = useState(false);

  const [newInteraction, setNewInteraction] = useState({
    type: '',
    title: '',
    description: '',
    assignee: '',
    due_date: null
  });

  useEffect(() => {
    fetchInteractions();
    fetchCurrentUser();
    fetchUsers();
  }, []);

    // Breadcrumbs
    useBreadcrumbs(
         [
            { label: 'Interaktionen' },
          ]
    );

  // Wenn ein Objekttyp gewählt wird, die zugehörigen Objekte laden
  useEffect(() => {
    if (!selectedObjectType) {
      setObjectList([]);
      setSelectedObjectId('');
      return;
    }
    const config = OBJECT_TYPES.find(t => t.value === selectedObjectType);
    if (!config) return;

    setObjectListLoading(true);
    setSelectedObjectId('');
    axiosInstance.get(config.endpoint)
      .then(res => setObjectList(res.data))
      .catch(err => {
        console.error('Error fetching objects:', err);
        showSnackbar('Fehler beim Laden der Objekte', 'error');
      })
      .finally(() => setObjectListLoading(false));
  }, [selectedObjectType]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axiosInstance.get('/me/');
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  const fetchInteractions = async () => {
    try {
      const response = await axiosInstance.get('/interactions/');
      const interactionsWithIds = response.data.map(interaction => ({
        ...interaction,
        id: interaction.id || Math.random()
      }));
      setInteractions(interactionsWithIds);
    } catch (error) {
      console.error('Error fetching interactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users/');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const getLeadName = (leadId) => {
    if (!leadId || !leads[leadId]) return '-';
    const lead = leads[leadId];
    return `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || lead.email || '-';
  };

  const getContactName = (contactId) => {
    if (!contactId || !contacts[contactId]) return '-';
    const contact = contacts[contactId];
    return contact.first_name && contact.last_name 
      ? `${contact.first_name} ${contact.last_name}`
      : contact.email || '-';
  };

  const handleOpenDialog = (interaction) => {
      setSelectedInteraction(interaction);
      setOriginalInteraction(JSON.parse(JSON.stringify(interaction)));
      setEditDialogOpen(true);
  };

  const hasChanges = () => {
    if (!selectedInteraction || !originalInteraction) return false;
    return (
      selectedInteraction.type !== originalInteraction.type ||
      selectedInteraction.title !== originalInteraction.title ||
      selectedInteraction.description !== originalInteraction.description ||
      selectedInteraction.due_date !== originalInteraction.due_date ||
      selectedInteraction.assignee !== originalInteraction.assignee
    );
  };

  const handleDeleteClick = (interaction) => {
    setInteractionToDelete(interaction);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!interactionToDelete) return;
    try {
      await axiosInstance.delete(`/interactions/${interactionToDelete.id}/`);
      setInteractions(interactions.filter(i => i.id !== interactionToDelete.id));
      setDeleteDialogOpen(false);
      setInteractionToDelete(null);
    } catch (error) {
      console.error('Error deleting interaction:', error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const getFilteredInteractions = () => {
    if (!currentUser) return [];
    return currentTab === 1
      ? interactions.filter(interaction => interaction.assignee === currentUser.id)
      : interactions;
  };

  const handleCompleteTask = async (interaction) => {
    try {
      const response = await axiosInstance.patch(`/interactions/${interaction.id}/`, {
        completed_at: new Date().toISOString()
      });
      setInteractions(interactions.map(i => i.id === interaction.id ? { ...i, completed_at: response.data.completed_at } : i));
    } catch (error) {
      console.error('Error completing task:', error);
    }
  };

  const handleCreateInteraction = async () => {
    try {
      const payload = {
        ...newInteraction,
        type: newInteraction.type || 'NOTE',
        ...(selectedObjectType && selectedObjectId
          ? { related_type: selectedObjectType, object_id: selectedObjectId }
          : {})
      };

      const response = await axiosInstance.post('/interactions/', payload);
      setInteractions([response.data, ...interactions]);
      setCreateDialogOpen(false);
      setNewInteraction({ type: '', title: '', description: '', assignee: '', due_date: null });
      setSelectedObjectType('');
      setSelectedObjectId('');
      showSnackbar('Interaktion erfolgreich erstellt', "success");
    } catch (error) {
      showSnackbar("Fehler beim Erstellen der Interaction", "error");
      console.error('Error creating interaction:', error);
    }
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setNewInteraction({ type: '', title: '', description: '', assignee: '', due_date: null });
    setSelectedObjectType('');
    setSelectedObjectId('');
  };

  const handleTypeSelect = (type) => {
    setNewInteraction({ ...newInteraction, type });
  };

  const handleEditTypeSelect = (type) => {
    setSelectedInteraction(prev => ({ ...prev, type }));
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setNewInteraction(prev => ({ ...prev, [name]: value }));
  };

  const handleEditChange = (event) => {
    setSelectedInteraction({ ...selectedInteraction, [event.target.name]: event.target.value });
  };

  const handleEditSaveInteraction = async () => {
    try {
      const response = await axiosInstance.put(
        `/interactions/${selectedInteraction.id}/`,
        {
          type: selectedInteraction.type,
          title: selectedInteraction.title,
          description: selectedInteraction.description,
          assignee: selectedInteraction.assignee,
          due_date: selectedInteraction.due_date,
          related_type: selectedInteraction.related_object_info?.type,
          object_id: selectedInteraction.related_object_info?.id,
        }
      );
      setInteractions(interactions.map(i => i.id === selectedInteraction.id ? response.data : i));
      setEditDialogOpen(false);
      setSelectedInteraction(null);
      showSnackbar("Interaktion erfolgreich aktualisiert", "success");
    } catch (error) {
      console.error('Error updating interaction:', error);
      showSnackbar("Fehler beim Erstellen der Interaction", "error");
    }
  };

  const getInteractionIcon = (type) => {
    switch (type) {
      case 'CALL': return <PhoneIcon />;
      case 'EMAIL': return <EmailIcon />;
      case 'TASK': return <TaskIcon />;
      case 'MEETING': return <MeetingRoomIcon />;
      case 'NOTE': return <NoteIcon />;
      default: return <NoteIcon />;
    }
  };

  const theme = useTheme();

  const interactionColorMap = {
    CALL: theme.palette.interaction.call,
    EMAIL: theme.palette.interaction.email,
    TASK: theme.palette.interaction.task,
    MEETING: theme.palette.interaction.meeting,
    NOTE: theme.palette.interaction.note,
  };

  const getInteractionColor = (type) =>
    interactionColorMap[type] || theme.palette.interaction.note;

  // Wiederverwendbare Typ-Auswahl-Leiste
  const TypeSelector = ({ value, onSelect }) => (
    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 2 }}>
      {['CALL', 'EMAIL', 'TASK', 'MEETING', 'NOTE'].map((t) => {
        const icons = { CALL: <PhoneIcon />, EMAIL: <EmailIcon />, TASK: <TaskIcon />, MEETING: <MeetingRoomIcon />, NOTE: <NoteIcon /> };
        const labels = { CALL: 'Anruf', EMAIL: 'E-Mail', TASK: 'Aufgabe', MEETING: 'Meeting', NOTE: 'Notiz' };
        return (
          <Tooltip title={labels[t]} key={t}>
            <IconButton
              onClick={() => onSelect(t)}
              sx={{
                backgroundColor: value === t ? getInteractionColor(t) : theme.palette.tokens.surface2,
                color: value === t ? 'white' : getInteractionColor(t),
                '&:hover': { backgroundColor: getInteractionColor(t), color: 'white' }
              }}
            >
              {icons[t]}
            </IconButton>
          </Tooltip>
        );
      })}
    </Box>
  );

  // Leerzustände (D4): erste Nutzung vs. leerer Tab „Meine Interaktionen"
  const NoRowsOverlay = () => (interactions.length > 0
    ? <EmptyState title="Nichts gefunden." hint="Such- oder Filterkriterien anpassen." />
    : (
      <EmptyState
        title="Noch keine Interaktionen."
        hint="Anrufe, Mails und Notizen erscheinen hier."
        actionLabel="Neue Interaktion"
        onAction={() => setCreateDialogOpen(true)}
      />
    ));
  const NoResultsOverlay = () => (
    <EmptyState title="Nichts gefunden." hint="Such- oder Filterkriterien anpassen." />
  );

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 50,
    },
    {
      field: 'actions',
      headerName: 'Aktionen',
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
          <Tooltip title={params.row.completed_at ? "Aufgabe bereits abgeschloßen" : "Aufgabe abschließen"}>
            <span>
              <IconButton
                onClick={() => handleCompleteTask(params.row)}
                color={params.row.completed_at ? "default" : "primary"}
                sx={{ width: 36, height: 36 }}
                disabled={Boolean(params.row.completed_at)}
              >
                <CheckCircleIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Bearbeiten">
            <IconButton
              variant="action-edit" // get style from theme.js
              onClick={() => handleOpenDialog(params.row)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Löschen">
            <IconButton
              variant="action-delete" // get style from theme.js
              onClick={() => handleDeleteClick(params.row)}
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Erstellt am',
      width: 120,
      renderCell: (params) => (
          new Date(params.value).toLocaleDateString('de-DE', { year: 'numeric', month: '2-digit', day: '2-digit' })
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => {
        const isCompleted = Boolean(params.row.completed_at);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', color: isCompleted ? 'success.main' : 'text.secondary' }}>
            {isCompleted ? <CheckCircleIcon sx={{ mr: 1 }} /> : <RadioButtonUncheckedIcon sx={{ mr: 1 }} />}
            {/* <Typography variant="body2">{isCompleted ? 'Erledigt' : 'Offen'}</Typography> */ }
          </Box>
        );
      }
    },
    {
      field: 'type_display',
      headerName: 'Typ',
      width: 60,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
          <Box sx={{
            color: 'white',
            backgroundColor: getInteractionColor(params.row.type),
            borderRadius: '50%',
            width: 32, height: 32,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            {getInteractionIcon(params.row.type)}
          </Box>
        </Box>
      )
    },
    {
      field: 'title',
      headerName: 'Titel',
      minWidth: 10,
      flex: 1,
    },
    {
      field: 'assignee_name',
      headerName: 'Zuweisung',
      width: 90,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%' }}>
          {params.value ? (
            <AvatarGroup
              max={5}
              sx={{
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  fontSize: '0.75rem',
                  backgroundColor: 'primary.main',
                },
                justifyContent: 'flex-start',
              }}
            >
              <Tooltip title={params.value} arrow placement="top">
                <Avatar alt={params.value}>
                  {getInitials(params.value)}
                </Avatar>
              </Tooltip>
            </AvatarGroup>
          ) : (
            <Typography variant="body2" color="text.secondary">-</Typography>
          )}
        </Box>
      ),
    },


    {
      field: 'related_object_info',
      headerName: 'Objekt',
      flex: 1,
      renderCell: (params) => {
        if (!params.value?.name) return <Typography variant="body2" color="text.secondary">-</Typography>;
        const typeConfig = OBJECT_TYPES.find(t => t.value === params.value?.type);
        return (
          <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', gap: 1 }}>
            {typeConfig && (
              
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28, height: 28,
                borderRadius: '50%',
                backgroundColor: alpha(typeConfig.color, 0.12),
                color: typeConfig.color,
                flexShrink: 0,
                '& svg': { fontSize: 16 }
              }}>
                <Tooltip
                  title={OBJECT_TYPES.find(obj => obj.value === params.value.type)?.label ?? ''}
                  arrow
                  placement="top"
                >
                  {typeConfig.icon}
                </Tooltip>
              </Box>
            )}
            <Typography variant="body2" noWrap>{params.value.name}</Typography>
          </Box>
        );
      }
    },
    {
      field: 'due_date',
      headerName: 'Fälligkeit',
      width: 120,
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
            new Date(params.value).toLocaleDateString('de-DE', { year: 'numeric', month: '2-digit', day: '2-digit' })
        );
      }
    },
  ];

  return (
    <>
      <Typography variant="h4" component="h2">Interaktionen</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Tabs value={currentTab} onChange={handleTabChange} aria-label="interaction tabs">
            <Tab label="Alle Interaktionen" />
            <Tab label="Meine Interaktionen" />
          </Tabs>
          <DataGrid
            rows={getFilteredInteractions()}
            columns={columns}
            loading={loading}
            pageSize={10}
            rowsPerPageOptions={[10]}
            disableSelectionOnClick
            autoHeight
            slots={{ noRowsOverlay: NoRowsOverlay, noResultsOverlay: NoResultsOverlay }}
            sx={{
              //'& .MuiDataGrid-cell': { py: 1},
              // Platz für den Leerzustand inkl. Aktion (Default: 2 Zeilenhöhen)
              '--DataGrid-overlayHeight': '180px',
              '& .completed-row': { bgcolor: 'action.hover', '&:hover': { bgcolor: 'action.selected' } }
            }}
            getRowClassName={(params) => params.row.completed_at ? 'completed-row' : ''}
          />
        </Grid>
      </Grid>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Interaktion löschen</DialogTitle>
        <DialogContent>
          Sind Sie sicher, dass Sie diese Interaktion löschen möchten?
          {interactionToDelete && (
            <Box sx={{ mt: 2, color: 'text.secondary' }}>
              <Typography variant="body2">Typ: {interactionToDelete.type_display}</Typography>
              <Typography variant="body2">Beschreibung: {interactionToDelete.description}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Löschen</Button>
        </DialogActions>
      </Dialog>

      {/* Create Interaction Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Neue Interaktion erstellen</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>

            {/* Typ-Auswahl */}
            <Typography variant="subtitle1">
              Typ <span style={{ color: theme.palette.error.main }}> *</span>
            </Typography>
            <TypeSelector value={newInteraction.type} onSelect={handleTypeSelect} />

            <TextField
              name="title"
              label="Titel"
              value={newInteraction.title}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              name="description"
              label="Beschreibung"
              multiline
              rows={4}
              value={newInteraction.description}
              onChange={handleInputChange}
              fullWidth
              required
            />

            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
              <DatePicker
                label="Fälligkeitsdatum"
                value={newInteraction.due_date ? dayjs(newInteraction.due_date) : null}
                onChange={(newValue) => {
                  if (newValue && !dayjs(newValue).isValid()) { setDateError('Ungültiges Datum'); return; }
                  setDateError(null);
                  handleInputChange({
                    target: {
                      name: 'due_date',
                      value: newValue && dayjs(newValue).isValid() ? newValue.toISOString() : null
                    }
                  });
                }}
                slotProps={{
                  field: { clearable: true },
                  textField: { fullWidth: true, error: !!dateError, helperText: dateError || '' }
                }}
              />
            </LocalizationProvider>

            <FormControl fullWidth>
              <InputLabel>Zuweisen an</InputLabel>
              <Select name="assignee" value={newInteraction.assignee} onChange={handleInputChange} label="Zuweisen an">
                <MenuItem value={currentUser?.id}>Mich</MenuItem>
                {users.filter(user => user.id !== currentUser?.id).map(user => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* ── Objektzuweisung ── */}
            <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2 }}>

              <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                Objekt zuweisen <span style={{ color: theme.palette.error.main }}> *</span>
              </Typography>

              {/* Schritt 1: Objekttyp */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {OBJECT_TYPES.map((ot) => (
                  <Chip
                    key={ot.value}
                    icon={ot.icon}
                    label={ot.label}
                    clickable
                    onClick={() => setSelectedObjectType(prev => prev === ot.value ? '' : ot.value)}
                    variant={selectedObjectType === ot.value ? 'filled' : 'outlined'}
                    sx={{
                      borderColor: selectedObjectType === ot.value ? ot.color : 'divider',
                      backgroundColor: selectedObjectType === ot.value ? ot.color : 'transparent',
                      color: selectedObjectType === ot.value ? 'white' : 'text.primary',
                      '& .MuiChip-icon': {
                        color: selectedObjectType === ot.value ? 'white' : ot.color
                      },
                      fontWeight: selectedObjectType === ot.value ? 600 : 400,
                      transition: 'all 0.15s ease',
                    }}
                  />
                ))}
              </Box>

              {/* Schritt 2: Spezifisches Objekt */}
              {selectedObjectType && (
                <FormControl fullWidth>
                  <InputLabel>
                    {OBJECT_TYPES.find(t => t.value === selectedObjectType)?.label} auswählen
                  </InputLabel>
                  <Select
                    value={selectedObjectId}
                    onChange={(e) => setSelectedObjectId(e.target.value)}
                    label={`${OBJECT_TYPES.find(t => t.value === selectedObjectType)?.label} auswählen`}
                    disabled={objectListLoading}
                  >
                    {objectListLoading && (
                      <MenuItem disabled value="">Lädt...</MenuItem>
                    )}
                    {!objectListLoading && objectList.length === 0 && (
                      <MenuItem disabled value="">Keine Einträge gefunden</MenuItem>
                    )}
                    {objectList.map((obj) => (
                      <MenuItem key={obj.id} value={obj.id}>
                        {getObjectLabel(obj, selectedObjectType)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Vorschau der gewählten Zuweisung */}
              {/* 
              {selectedObjectType && selectedObjectId && (() => {
                const obj = objectList.find(o => o.id === selectedObjectId || String(o.id) === String(selectedObjectId));
                const typeConfig = OBJECT_TYPES.find(t => t.value === selectedObjectType);
                return obj ? (
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1, mt: 1.5,
                    p: 1.5, borderRadius: 1,
                    backgroundColor: `${typeConfig.color}15`,
                    border: `1px solid ${typeConfig.color}40`
                  }}>
                    <Box sx={{ color: typeConfig.color, display: 'flex' }}>{typeConfig.icon}</Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {typeConfig.label}: {getObjectLabel(obj, selectedObjectType)}
                    </Typography>
                  </Box>
                ) : null;
              })()}
              */}

            </Box>

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Abbrechen</Button>
          <Button
            onClick={handleCreateInteraction}
            variant="contained"
            color="primary"
            disabled={!newInteraction.title || !newInteraction.type || !newInteraction.description || !selectedObjectType || !selectedObjectId}
          >
            Erstellen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Interaction Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Interaktion bearbeiten</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Typography variant="subtitle1">Typ <span style={{ color: theme.palette.error.main }}> *</span></Typography>
            <TypeSelector value={selectedInteraction?.type} onSelect={handleEditTypeSelect} />

            <TextField
              name="title"
              label="Titel"
              value={selectedInteraction?.title}
              onChange={handleEditChange}
              fullWidth
              required
            />
            <TextField
              name="description"
              label="Beschreibung"
              multiline
              rows={4}
              value={selectedInteraction?.description}
              onChange={handleEditChange}
              fullWidth
              required
            />

            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
              <DatePicker
                label="Fälligkeitsdatum"
                value={selectedInteraction?.due_date ? dayjs(selectedInteraction.due_date) : null}
                onChange={(newValue) => {
                  if (newValue && !dayjs(newValue).isValid()) { setDateError('Ungültiges Datum'); return; }
                  setDateError(null);
                  handleEditChange({
                    target: {
                      name: 'due_date',
                      value: newValue && dayjs(newValue).isValid() ? newValue.toISOString() : null
                    }
                  });
                }}
                slotProps={{
                  field: { clearable: true },
                  textField: { fullWidth: true, error: !!dateError, helperText: dateError || '' }
                }}
              />
            </LocalizationProvider>

            <FormControl fullWidth>
              <InputLabel>Zuweisen an</InputLabel>
              <Select name="assignee" value={selectedInteraction?.assignee} onChange={handleEditChange} label="Zuweisung an">
                <MenuItem value={currentUser?.id}>Mich</MenuItem>
                {users.filter(user => user.id !== currentUser?.id).map(user => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={handleEditSaveInteraction}
            variant="contained"
            color="primary"
            disabled={!hasChanges()}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        sx={{ position: 'fixed', bottom: 24, right: 24 }}
        onClick={() => setCreateDialogOpen(true)}
      >
        <AddIcon />
      </Fab>
    </>
  );
}

export default InteractionList;